import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import Loading from '../../Shared/Loading';
import { useNotification } from '../../../context/NotificationContext';
import { City, ExtractedEducation, State, University } from '../../../Models';
import { v4 as uuidv4 } from 'uuid';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { GetAllUnisByName } from '../../../api/masterdata.api';
import useApiToken from '../../../hooks/useApiToken';


export default function NewEducationModal({
  size,
  isOpen,
  onCancel,
  isModalConfirm,
  education,
  cities,
  states
}: {
  cities: City[],
  states: State[],
  onCancel: Function,
  isOpen: boolean,
  isModalConfirm: Function,
  size: string,
  education: ExtractedEducation & { id: string } | null | undefined
}) {
  const token = useApiToken();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [universities, setUniversities] = useState<University[]>([]);
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);

  // Form states...
  const [universitySearch, setUniversitySearch] = useState('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [universityUrl, setUniversityUrl] = useState('');
  const [fromYear, setFromYear] = useState('');
  const [toYear, setToYear] = useState('');
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [showUniversityDropdown, setShowUniversityDropdown] = useState(false);

  // Notification
  const { error: notifyError } = useNotification();
  const cancelButtonRef = useRef(null);
  const [isUnderGrad, setIsUnderGrad] = useState<boolean>(true);
  const [isPostGrad, setIsPostGrad] = useState<boolean>(false);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  // Initialize form data
  useEffect(() => {
    if (education) {
      setUniversitySearch(education.school || '');
      setUniversityUrl(education.url || '');

      const [from, to] = (education.period || '').split(' - ');
      if(from==="MM/YY"){
        setFromYear('');
      }
      else{
        setFromYear(from || '');
      }
      setToYear(to || '');

      if (education.city) {
        const cityObj = cities.find(c => c.name === education.city.split(',')[0]);
        if (cityObj?.state) {
          setSelectedState(cityObj.state.id);
          setSelectedCity(cityObj.id);
          setAvailableCities(cities.filter(c => c.state?.id === cityObj.state?.id));
        }
      }
    }
  }, [education, cities]);

  // Handle university search
  const handleUniversitySearch = async (searchTerm: string) => {
    if (searchTerm === '') {
      setSelectedUniversity(null);
      setSelectedState('');
      setSelectedCity('');
      setUniversityUrl('');
    }
    setUniversitySearch(searchTerm);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (searchTerm.length < 3) {
      setUniversities([]);
      setShowUniversityDropdown(false);
      return;
    }

    setLoading(true);
    const timeout = setTimeout(async () => {
      if (!token) return;
      try {
        const results = await GetAllUnisByName(searchTerm, token);
        setUniversities(results);
        setShowUniversityDropdown(true);
      } catch (err) {
        console.error('Error searching universities:', err);
        notifyError('Error', 'Error searching universities');
      } finally {
        setLoading(false);
      }
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleUniversitySelect = (university: University) => {
    setSelectedUniversity(university);
    setUniversitySearch(university.name);
    setSelectedState(university.state?.id || '');
    setSelectedCity(university.city?.id || '');
    setUniversityUrl(university.url || '');
    setShowUniversityDropdown(false);

    setAvailableCities(cities.filter(city => city.state?.id === university.state?.id));
  };

  const handleSubmit = () => {
    if (!universitySearch || !selectedState || !selectedCity || !fromYear || !toYear || !universityUrl) {
      notifyError('Error', 'Please fill in all required fields');
      return;
    }

    if (selectedUniversity) {
      // If a university was selected from the search, return the university object
      isModalConfirm(selectedUniversity, `${fromYear} - ${toYear}`, education?.id, "old", isPostGrad);
    } else {
      const formData = {
        id: education?.id,
        name: universitySearch,
        stateId: selectedState,
        cityId: selectedCity,
        url: universityUrl,
        isConfirmed: true,
        isRejected: false,
        abbreviation: '',
        city: cities.find(c => c.id === selectedCity)?.name,
        state: states.find(c => c.id === selectedState)?.name,
        period: `${fromYear} - ${toYear}`,
      };
      isModalConfirm(formData, `${fromYear} - ${toYear}`, education?.id, "new", isPostGrad);
    }
    setOpen(false);
  };
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10 dark:bg-entntdarkblue"
        initialFocus={cancelButtonRef}
        onClose={() => {
          onCancel();
          setOpen(false);
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-400 bg-opacity-50 dark:bg-opacity-20 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full h-fit flex flex-col dark:bg-darkbg sm:max-w-lg">
                <div className="bg-gray-200 h-12 p-4 dark:bg-darkbglight flex flex-row items-center justify-between">
                  <h2 className="font-bold text-gray-600 text-sm text-left dark:text-gray-300 ">
                    Add University Details
                  </h2>
                </div>
                <div className="p-6 space-y-4">
                  {/* University Search Input */}
                  <div className="relative">
                    <div className='flex flex-row items-center justify-between'>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        University Name *
                      </label>
                      <div className='flex flex-row items-center gap-2'>
                        <div className='flex flex-row items-center dark:text-gray-300 text-gray-600'>
                          <label htmlFor="undergrad">UG</label>
                          <input type="checkbox" className='ml-4' id='undergrad' checked={isUnderGrad} onChange={(e) => {
                            if (e.target.checked) {
                              setIsUnderGrad(true);
                              setIsPostGrad(false);
                            } else {
                              setIsPostGrad(true);
                              setIsUnderGrad(false);
                            }
                          }} />
                        </div>
                        <div className='flex flex-row items-center dark:text-gray-300 text-gray-600'>
                          <label htmlFor="postgrad">PG</label>
                          <input type="checkbox" name="" id="postgrad" className='ml-4' checked={isPostGrad} onChange={(e) => {
                            if (e.target.checked) {
                              setIsPostGrad(true);
                              setIsUnderGrad(false);
                            } else {
                              setIsUnderGrad(true);
                              setIsPostGrad(false);
                            }
                          }} />

                        </div>
                      </div>
                    </div>

                    <input
                      type="text"
                      value={universitySearch}
                      onChange={(e) => handleUniversitySearch(e.target.value)}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-darkbglight dark:border-gray-600 dark:text-gray-300"
                      placeholder="Search for university..."
                    />
                    {/* University Search Results Dropdown */}
                    {showUniversityDropdown && universities.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-darkbglight border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {universities.map((uni) => (
                          <div
                            key={uni.id}
                            className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                            onClick={() => handleUniversitySelect(uni)}
                          >
                            <div className="font-medium dark:text-gray-300">{uni.name}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {uni.city && uni.city.name}, {uni.state && uni.state.name}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* University URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      University URL
                    </label>
                    <input
                      type="text"
                      value={universityUrl}
                      onChange={(e) => setUniversityUrl(e.target.value)}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-darkbglight dark:border-gray-600 dark:text-gray-300"
                      placeholder="eg. www.universityname.com"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 ">
                    <div className='flex flex-col '> 
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        From Year *
                      </label>
                      <DatePicker
                        selected={fromYear ? new Date(parseInt(fromYear), 0, 1) : null}
                        onChange={(date) => setFromYear(date ? date.getFullYear().toString() : "")}
                        showYearPicker
                        dateFormat="yyyy"
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-darkbglight dark:border-gray-600 dark:text-gray-300"
                        placeholderText="YYYY"
                      />
                    </div>
                    <div className='flex flex-col'>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        To Year *
                      </label>
                      <DatePicker
                        selected={
                          toYear && toYear.toLocaleLowerCase() !== "present"
                            ? new Date(parseInt(toYear), 0, 1)
                            : null
                        }
                        onChange={(date) => {
                          if (date) {
                            setToYear(date.getFullYear().toString());
                          } else {
                            setToYear(""); 
                          }
                        }}
                        showYearPicker
                        dateFormat="yyyy"
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-darkbglight dark:border-gray-600 dark:text-gray-300"
                        placeholderText="YYYY"
                        minDate={
                          fromYear && fromYear !== "Present"
                            ? new Date(parseInt(fromYear) + 3, 0, 1)
                            : null
                        }
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      State *
                    </label>
                    <select
                      value={selectedState}
                      onChange={(e) => {
                        setSelectedState(e.target.value);
                        setAvailableCities(cities.filter(city => city.state?.id === e.target.value));
                      }}

                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-darkbglight dark:border-gray-600 dark:text-gray-300"
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* City Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      City *
                    </label>
                    <select
                      value={selectedCity}
                      onChange={(e) => setSelectedCity(e.target.value)}
                      disabled={!selectedState}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-darkbglight dark:border-gray-600 dark:text-gray-300"
                    >
                      <option value="">Select City</option>
                      {availableCities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className={`bg-gray-50 px-4 py-3 sm:flex sm:px-6 dark:bg-darkbglight flex flex-row items-center justify-between `}>
                  <div>
                    {!selectedUniversity && <span className='text-red-500 text-xs'>New University will be created. </span>}
                  </div>
                  <div className='flex  items-center gap-2 flex-row-reverse'>
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                      onClick={handleSubmit}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => {
                        onCancel();
                        setOpen(false);
                      }}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                  </div>
                </div>

              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}